/**
 * hardwareAdapter.ts — Capa de abstracción de hardware multiplataforma
 *
 * QUÉ HACE:
 *  - Centraliza todo acceso a periféricos físicos (impresora, báscula, gaveta).
 *  - Resuelve la plataforma y ejecuta la instrucción correcta:
 *    · Native (Android/iOS): plugins Capacitor / AltenotPos
 *    · Desktop Tauri — Red: invoke print_raw_tcp → Rust abre socket TCP
 *    · Desktop Tauri — USB:  invoke print_via_port     → Rust escribe a \\.\USB001
 *    · Desktop Tauri — BT:   invoke print_via_com_port → serialport crate → \\.\COM3
 *    · Web — USB (Chrome/Edge): WebUSB API — requiere HTTPS o localhost
 *    · Web — Red (relay):    fetch POST a relay HTTP local → reenvía al TCP
 *    · Web — otras: no-op con mensaje de error claro
 *
 * QUÉ EXPORTA: hardwareAdapter (singleton)
 *
 * QUÉ NO DEBE HACER:
 *  - No renderizar JSX ni importar componentes
 *  - No contener lógica de negocio (precios, descuentos, flujos de venta)
 */

import { isDesktop, isNative } from "@/lib/platform";

// ── Tipos ─────────────────────────────────────────────────────────────────────

export interface PrintResult {
  success: boolean;
  error?: string;
}

export interface ScaleResult {
  weight: number | null;
  unit: "kg" | "g" | "lb";
  error?: string;
}

export interface CashDrawerResult {
  opened: boolean;
  error?: string;
}

export interface BarcodeResult {
  code: string | null;
  format?: string;
  error?: string;
}

// ── Tipos mínimos WebUSB (evita dependencia @types/wicg-webusb) ──────────────

interface WebUsbEndpoint {
  direction: "in" | "out";
  type: "bulk" | "interrupt" | "isochronous";
  endpointNumber: number;
}
interface WebUsbInterface {
  interfaceNumber: number;
  alternates: Array<{ endpoints: WebUsbEndpoint[] }>;
}
interface WebUsbDevice {
  vendorId: number;
  productId: number;
  opened: boolean;
  configuration: { interfaces: WebUsbInterface[] } | null;
  open(): Promise<void>;
  close(): Promise<void>;
  selectConfiguration(n: number): Promise<void>;
  claimInterface(n: number): Promise<void>;
  releaseInterface(n: number): Promise<void>;
  transferOut(
    endpointNumber: number,
    data: BufferSource,
  ): Promise<{ status: string }>;
}
interface WebUsbApi {
  getDevices(): Promise<WebUsbDevice[]>;
  requestDevice(o: {
    filters?: object[];
  }): Promise<WebUsbDevice & { productName?: string }>;
}

// ── WebUSB helper (web Chrome / Edge — localhost o HTTPS) ─────────────────────

/**
 * Imprime via WebUSB. Funciona en Chrome/Edge en producción (HTTPS) o localhost.
 * storedDeviceKey: "vendorId:productId" guardado al seleccionar el dispositivo.
 */
async function printViaWebUsb(
  escPosBytes: Uint8Array,
  storedDeviceKey: string,
): Promise<PrintResult> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- WebUSB no está en los tipos estándar de TS
    const usb = (navigator as any).usb as WebUsbApi | undefined;
    if (!usb) {
      return {
        success: false,
        error:
          "WebUSB no disponible. Usa Chrome/Edge en HTTPS o instala la app de escritorio.",
      };
    }
    const devices = await usb.getDevices();
    const device = devices.find(
      (d) => `${d.vendorId}:${d.productId}` === storedDeviceKey,
    );
    if (!device) {
      return {
        success: false,
        error:
          "Impresora USB no encontrada. Vuelve a seleccionarla en Ajustes → Impresora POS.",
      };
    }
    if (!device.opened) await device.open();
    if (device.configuration === null) await device.selectConfiguration(1);
    const iface = device.configuration?.interfaces[0];
    if (iface) await device.claimInterface(iface.interfaceNumber);
    const endpoint = iface?.alternates[0]?.endpoints.find(
      (e) => e.direction === "out" && e.type === "bulk",
    );
    if (!endpoint) {
      if (iface) await device.releaseInterface(iface.interfaceNumber);
      await device.close();
      return {
        success: false,
        error: "No se encontró endpoint de impresión en el dispositivo USB",
      };
    }
    const xfer = await device.transferOut(
      endpoint.endpointNumber,
      escPosBytes as unknown as ArrayBuffer,
    );
    if (iface) await device.releaseInterface(iface.interfaceNumber);
    await device.close();
    if (xfer.status !== "ok") {
      return {
        success: false,
        error: `USB transfer status: ${xfer.status}. Verifica que la impresora esté lista.`,
      };
    }
    return { success: true };
  } catch (err) {
    const msg = String(err);
    if (msg.includes("Access denied") || msg.includes("SecurityError")) {
      return {
        success: false,
        error:
          "Acceso denegado al USB. El driver de la impresora en Windows bloquea el acceso desde el navegador. Usa la app de escritorio o el modo Red (Wi-Fi) con relay local.",
      };
    }
    return { success: false, error: msg };
  }
}

// ── Helpers internos ──────────────────────────────────────────────────────────

/** Carga la config de impresora. Import dinámico para evitar ciclos. */
async function getPrinterCfg() {
  const { loadPosPrinterConfigFromStorage } =
    await import("@/lib/native/posPrinterConfig");
  return loadPosPrinterConfigFromStorage();
}

/** Resuelve la config efectiva según destino de impresión (tickets/labels). */
async function getPrinterCfgForTarget(
  target: "tickets" | "labels",
): Promise<Awaited<ReturnType<typeof getPrinterCfg>>> {
  const cfg = await getPrinterCfg();
  if (target === "labels") {
    const { resolvePrinterConfig } =
      await import("@/lib/native/posPrinterConfig");
    return resolvePrinterConfig(cfg, "labels");
  }
  return cfg;
}

/** Invoca un comando Tauri. Import dinámico para que web no lo falle. */
async function tauriInvoke<T>(
  cmd: string,
  args?: Record<string, unknown>,
): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args);
}

// ── Relay HTTP para web WiFi ───────────────────────────────────────────────────

/**
 * Envía bytes ESC/POS a la impresora a través de un relay HTTP local.
 *
 * El navegador web no puede abrir sockets TCP directamente. Un pequeño servidor
 * relay corriendo en la misma red (o el mismo PC) recibe la petición HTTP y la
 * reenvía al socket TCP de la impresora.
 *
 * Protocolo del relay:
 *   POST {relayUrl}/print?host={printerHost}&port={printerPort}
 *   Content-Type: application/octet-stream
 *   Body: bytes ESC/POS en crudo
 *
 * Para abrir la gaveta:
 *   POST {relayUrl}/drawer?host={printerHost}&port={printerPort}
 *   Body: vacío (el relay envía el kick-pulse ESC/POS)
 */
async function printViaRelay(
  relayUrl: string,
  printerHost: string,
  printerPort: number,
  escPosBytes: Uint8Array,
): Promise<PrintResult> {
  try {
    const url = `${relayUrl.replace(/\/$/, "")}/print?host=${encodeURIComponent(printerHost)}&port=${printerPort}`;
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: escPosBytes as unknown as BodyInit,
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      return {
        success: false,
        error: `Relay respondió ${resp.status}: ${text || resp.statusText}`,
      };
    }
    return { success: true };
  } catch (err) {
    return {
      success: false,
      error: `No se pudo conectar al relay (${relayUrl}). Verifica que esté ejecutándose. Error: ${String(err)}`,
    };
  }
}

async function drawerViaRelay(
  relayUrl: string,
  printerHost: string,
  printerPort: number,
): Promise<CashDrawerResult> {
  try {
    const url = `${relayUrl.replace(/\/$/, "")}/drawer?host=${encodeURIComponent(printerHost)}&port=${printerPort}`;
    const resp = await fetch(url, { method: "POST" });
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      return {
        opened: false,
        error: `Relay respondió ${resp.status}: ${text || resp.statusText}`,
      };
    }
    return { opened: true };
  } catch (err) {
    return {
      opened: false,
      error: `No se pudo conectar al relay (${relayUrl}). Error: ${String(err)}`,
    };
  }
}

// ── Adaptador de impresora térmica ────────────────────────────────────────────

const printer = {
  /**
   * Imprime un ticket ESC/POS.
   * · Native: AltenotPos (BT / USB OTG / Wi-Fi vía plugin Capacitor)
   * · Desktop — Red:  Rust TCP → IP:9100
   * · Desktop — USB:  Rust print_via_port → \\.\USB001 (sin drivers Zadig)
   * · Desktop — BT:   Rust print_via_com_port → serialport COM3 (más fiable que raw file)
   * · Web — USB:      WebUSB API (Chrome/Edge, HTTPS o localhost)
   * · Web — Red:      fetch POST → relay HTTP local → TCP a la impresora
   * · Web — otros:    no-op con mensaje de error claro
   *
   * @param target "tickets" (impresora principal) o "labels" (impresora de
   * etiquetas si está configurada como separada; si no, la principal).
   */
  async printRaw(
    escPosBytes: Uint8Array,
    target: "tickets" | "labels" = "tickets",
  ): Promise<PrintResult> {
    try {
      if (isNative) {
        const { AltenotPos } = await import("@/lib/native/altenotPos");
        const binary = Array.from(escPosBytes)
          .map((b) => String.fromCharCode(b))
          .join("");
        await AltenotPos.printEscPosBase64({ dataBase64: btoa(binary) });
        return { success: true };
      }

      if (isDesktop) {
        const cfg = await getPrinterCfgForTarget(target);

        // Red (Wi-Fi / TCP directo via Rust)
        if (cfg.transport === "network" && cfg.networkHost.trim()) {
          await tauriInvoke("print_raw_tcp", {
            host: cfg.networkHost.trim(),
            port: cfg.networkPort || 9100,
            data: Array.from(escPosBytes),
          });
          return { success: true };
        }

        // USB: raw file write a \\.\USB001 (sin driver Zadig, solo impresora instalada en Windows)
        if (cfg.transport === "usb") {
          const portName = cfg.usbDeviceKey.trim();
          if (!portName) {
            return {
              success: false,
              error:
                "Configura el puerto USB en Preferencias → Impresora POS. Pulsa «Buscar puertos USB» o escribe el nombre (ej. USB001).",
            };
          }
          if (/^\d+:\d+$/.test(portName)) {
            return {
              success: false,
              error: `El campo Puerto contiene un ID de WebUSB (${portName}). Ve a Preferencias → Impresora POS, borra ese valor y pulsa «Buscar puertos USB» o escribe el nombre del puerto (ej. USB001).`,
            };
          }
          await tauriInvoke("print_via_port", {
            portName,
            data: Array.from(escPosBytes),
          });
          return { success: true };
        }

        // Bluetooth SPP: puerto COM serie via serialport crate (más fiable que raw file)
        if (cfg.transport === "bluetooth_spp") {
          const portName = cfg.bluetoothMac.trim();
          if (!portName) {
            return {
              success: false,
              error:
                "Configura el puerto COM Bluetooth en Preferencias → Impresora POS. Empareja la impresora en Ajustes → Bluetooth de Windows y pulsa «Buscar».",
            };
          }
          await tauriInvoke("print_via_com_port", {
            portName,
            data: Array.from(escPosBytes),
          });
          return { success: true };
        }

        return {
          success: false,
          error:
            "Sin impresora configurada — ve a Preferencias → Impresora POS.",
        };
      }

      // ── Web ──────────────────────────────────────────────────────────────────
      const cfg = await getPrinterCfgForTarget(target);

      // USB via WebUSB (Chrome/Edge — requiere HTTPS o localhost)
      if (cfg.transport === "usb" && cfg.usbDeviceKey.trim()) {
        if (!window.isSecureContext) {
          return {
            success: false,
            error:
              "WebUSB requiere una conexión segura (HTTPS o localhost). Accede a la app por HTTPS para usar la impresora USB desde el navegador.",
          };
        }
        return await printViaWebUsb(escPosBytes, cfg.usbDeviceKey.trim());
      }

      // Red via relay HTTP local
      if (
        cfg.transport === "network" &&
        cfg.networkHost.trim() &&
        cfg.networkRelayUrl.trim()
      ) {
        return await printViaRelay(
          cfg.networkRelayUrl.trim(),
          cfg.networkHost.trim(),
          cfg.networkPort || 9100,
          escPosBytes,
        );
      }

      // Sin ruta disponible en web
      if (cfg.transport === "network") {
        return {
          success: false,
          error:
            "Para imprimir por Wi-Fi desde el navegador configura la URL del relay local en Preferencias → Impresora POS.",
        };
      }
      if (cfg.transport === "bluetooth_spp") {
        return {
          success: false,
          error:
            "Bluetooth no está disponible en el navegador web. Usa la app de escritorio o la APK para imprimir via Bluetooth.",
        };
      }

      return {
        success: false,
        error:
          cfg.transport === "none"
            ? "Sin impresora configurada — ve a Preferencias → Impresora POS."
            : "Impresión no disponible en web para este modo. Usa la app de escritorio o la APK.",
      };
    } catch (err) {
      return { success: false, error: String(err) };
    }
  },

  /**
   * Abre la gaveta de efectivo (kick-pulse ESC/POS).
   * · Native:         AltenotPos.openCashDrawer()
   * · Desktop — Red:  Rust TCP kick pulse
   * · Desktop — USB:  Rust print_via_port kick pulse
   * · Desktop — BT:   Rust print_via_com_port kick pulse (serialport)
   * · Web — USB:      WebUSB kick pulse
   * · Web — Red:      relay HTTP kick pulse
   */
  async openCashDrawer(): Promise<CashDrawerResult> {
    try {
      if (isNative) {
        const { AltenotPos } = await import("@/lib/native/altenotPos");
        await AltenotPos.openCashDrawer();
        return { opened: true };
      }

      if (isDesktop) {
        const cfg = await getPrinterCfg();

        if (cfg.transport === "network" && cfg.networkHost.trim()) {
          await tauriInvoke("open_cash_drawer_tcp", {
            host: cfg.networkHost.trim(),
            port: cfg.networkPort || 9100,
          });
          return { opened: true };
        }

        if (cfg.transport === "usb") {
          const portName = cfg.usbDeviceKey.trim();
          if (!portName || /^\d+:\d+$/.test(portName)) {
            return {
              opened: false,
              error: !portName
                ? "Configura el puerto USB en Preferencias → Impresora POS."
                : `ID de WebUSB detectado (${portName}). Borra el campo y escribe el puerto Windows (ej. USB001).`,
            };
          }
          await tauriInvoke("open_cash_drawer_via_port", { portName });
          return { opened: true };
        }

        if (cfg.transport === "bluetooth_spp") {
          const portName = cfg.bluetoothMac.trim();
          if (!portName) {
            return {
              opened: false,
              error:
                "Configura el puerto COM Bluetooth en Preferencias → Impresora POS.",
            };
          }
          await tauriInvoke("open_cash_drawer_com_port", { portName });
          return { opened: true };
        }

        return {
          opened: false,
          error:
            "Configura una impresora en Preferencias → Impresora POS. La gaveta se conecta a la impresora via cable kick (RJ11).",
        };
      }

      // ── Web ──────────────────────────────────────────────────────────────────
      const cfg = await getPrinterCfg();

      if (cfg.transport === "usb" && cfg.usbDeviceKey.trim()) {
        if (!window.isSecureContext) {
          return {
            opened: false,
            error:
              "WebUSB requiere HTTPS o localhost para funcionar desde el navegador.",
          };
        }
        const KICK = new Uint8Array([0x1b, 0x70, 0x00, 0x19, 0xfa]);
        const r = await printViaWebUsb(KICK, cfg.usbDeviceKey.trim());
        return { opened: r.success, error: r.error };
      }

      if (
        cfg.transport === "network" &&
        cfg.networkHost.trim() &&
        cfg.networkRelayUrl.trim()
      ) {
        return await drawerViaRelay(
          cfg.networkRelayUrl.trim(),
          cfg.networkHost.trim(),
          cfg.networkPort || 9100,
        );
      }

      return { opened: false, error: "no-cash-drawer-in-web" };
    } catch (err) {
      return { opened: false, error: String(err) };
    }
  },
};

// ── Adaptador de báscula serial ───────────────────────────────────────────────

const scale = {
  async getWeight(): Promise<ScaleResult> {
    try {
      if (isNative) {
        return {
          weight: null,
          unit: "kg",
          error: "scale-not-implemented-native",
        };
      }
      if (isDesktop) {
        const result = await tauriInvoke<{ weight: number; unit: string }>(
          "read_scale_weight",
        );
        return {
          weight: result.weight,
          unit: result.unit as "kg" | "g" | "lb",
        };
      }
      return { weight: null, unit: "kg", error: "no-scale-in-web" };
    } catch (err) {
      return { weight: null, unit: "kg", error: String(err) };
    }
  },
};

// ── Adaptador de escáner ──────────────────────────────────────────────────────

const scanner = {
  /** En desktop los escáneres USB se presentan como teclado HID — useBarcodeScanner.ts ya lo maneja. */
  async scan(): Promise<BarcodeResult> {
    return { code: null, error: "use-keyboard-hid-scanner" };
  },
};

// ── Adaptador de notificaciones del sistema ───────────────────────────────────

const notifications = {
  async show(title: string, body: string): Promise<void> {
    try {
      if (isDesktop) {
        const { sendNotification } =
          await import("@tauri-apps/plugin-notification");
        sendNotification({ title, body });
        return;
      }
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(title, { body });
      }
    } catch {
      /* no crítico */
    }
  },
};

// ── Singleton exportado ───────────────────────────────────────────────────────

export const hardwareAdapter = {
  printer,
  scale,
  scanner,
  notifications,
} as const;
